import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { createTeamUseCase } from '@/src/core/dependencies';
import { ModerationError } from '@/src/domain/models/Moderation';
import { useTranslation } from '@/src/i18n';
import { CreateTeamScreen } from '@/src/presentation/screens/CreateTeamScreen';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';

export default function CreateTeamContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { checkAccess } = useSubscription();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [creating, setCreating] = useState(false);

  const { pickAndUploadImage: pickLogo, uploading: uploadingLogo } = useImageUpload({
    bucket: 'team-logos',
    userId: user?.id || '',
    preset: 'avatar',
    fieldType: 'avatar_image',
    onSuccess: setLogoUrl,
  });

  const handleCreate = async () => {
    if (!user) return;
    if (!name.trim()) {
      platformAlert(t('common.error'), t('teams.teamName'));
      return;
    }

    // Feature gating: check team limit
    const access = await checkAccess({ feature: 'create_team' });
    if (!access.allowed) {
      platformAlert(t('subscription.featureLocked'), t('subscription.teamLimitReached'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
      ]);
      return;
    }

    try {
      setCreating(true);
      const team = await createTeamUseCase.execute({
        name: name.trim(),
        description: description.trim() || undefined,
        logoUrl: logoUrl || undefined,
        isPublic,
        userId: user.id,
      });

      router.replace(`/team/${team.id}`);
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('CreateTeam', 'Failed to create team', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: t('teams.createTeam') }} />
      <CreateTeamScreen
        name={name}
        description={description}
        logoUrl={logoUrl || null}
        isPublic={isPublic}
        creating={creating}
        uploadingLogo={uploadingLogo}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onIsPublicChange={setIsPublic}
        onUploadLogo={pickLogo}
        onCreate={handleCreate}
        onCancel={() => router.back()}
        colors={colors}
      />
    </>
  );
}
